import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import {
  PencilSimpleIcon, SpinnerGapIcon, ImageIcon, XLogoIcon, CopyIcon,
  ArrowClockwiseIcon, UploadSimpleIcon, TrashIcon, MagnifyingGlassIcon,
  ChartLineUpIcon, ArticleIcon, ListNumbersIcon,
} from "@phosphor-icons/react";
import { generateCustomEditorial, fetchCustomStoryJob, fetchLatestCustomEditorial, fetchCustomStory, fetchPriceSeries } from "../lib/api";
import { renderPriceChart } from "../lib/miniChart";
import RugTimeline from "./RugTimeline";
import StoryThread from "./StoryThread";
import StoryLibrary from "./StoryLibrary";

// --- minimal, safe markdown → React ---
const inline = (text, kp) => {
  const parts = []; let rest = text, idx = 0;
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/; let m;
  while ((m = re.exec(rest))) {
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    if (m[2] != null) parts.push(<strong key={`${kp}-b${idx}`} className="text-white font-semibold">{m[2]}</strong>);
    else parts.push(<em key={`${kp}-i${idx}`} className="text-white/90">{m[3]}</em>);
    rest = rest.slice(m.index + m[0].length); idx++;
  }
  if (rest) parts.push(rest);
  return parts;
};
const renderMd = (md) =>
  md.split(/\n{2,}/).map((blk, i) => {
    const t = blk.trim();
    if (!t) return null;
    if (t === "---" || t === "***") return <hr key={i} className="border-white/10 my-4" />;
    if (t.startsWith("### ")) return <h3 key={i} className="font-heading font-bold text-lg mt-4 mb-1">{inline(t.slice(4), i)}</h3>;
    if (t.startsWith("## ")) return <h2 key={i} className="font-heading font-black text-2xl tracking-tight mb-2">{inline(t.slice(3), i)}</h2>;
    if (t.startsWith("# ")) return <h2 key={i} className="font-heading font-black text-2xl tracking-tight mb-2">{inline(t.slice(2), i)}</h2>;
    return <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">{t.split("\n").map((ln, j) => <span key={j}>{inline(ln.replace(/^[-*]\s/, "• "), `${i}-${j}`)}<br /></span>)}</p>;
  });

const cleanHeadline = (title = "") => {
  const parts = title.split(/\s+[—–-]\s+/);
  if (parts.length > 1) {
    const last = parts[parts.length - 1];
    if (/UTC|\b20\d{2}\b|\d{1,2}:\d{2}/.test(last)) return parts.slice(0, -1).join(" — ").trim();
  }
  return title.trim();
};
const pullHook = (body = "") => {
  const plain = body.replace(/===/g, " ").replace(/^#.*$/gm, "").replace(/[#*_>`]/g, "").replace(/\s+/g, " ").trim();
  const sentences = plain.split(/(?<=[.!?])\s+/);
  let out = "";
  for (const s of sentences) {
    const t = s.trim();
    if (!t) continue;
    if (out && (out + " " + t).length > 170) break;
    out = out ? `${out} ${t}` : t;
    if (out.length >= 60) break;
  }
  if (!out) out = plain.slice(0, 167);
  return out.length > 170 ? out.slice(0, 167) + "…" : out;
};

const ANGLES = [
  { k: "case-study", label: "Case study" },
  { k: "cautionary", label: "Cautionary tale" },
  { k: "explainer", label: "Explainer" },
  { k: "hype-check", label: "Hype-check" },
];

export default function CustomStory() {
  const [subject, setSubject] = useState("");
  const [address, setAddress] = useState("");
  const [angle, setAngle] = useState("case-study");
  const [mode, setMode] = useState("story");
  const [chart, setChart] = useState(null);
  const [series, setSeries] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [busy, setBusy] = useState(false);
  const [grabbing, setGrabbing] = useState(false);
  const [piece, setPiece] = useState(null);
  const [libRefresh, setLibRefresh] = useState(0);
  const fileRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    fetchLatestCustomEditorial().then((d) => { if (d?.body) hydrate(d); }).catch(() => {});
  }, []);

  const hydrate = (d) => {
    setPiece(d);
    setMode(d.mode === "thread" ? "thread" : "story");
    if (d.address) loadSeries(d.address);
    else { setSeries(null); setTimeline(null); }
  };

  const loadSeries = async (addr) => {
    try {
      const d = await fetchPriceSeries(addr, "5m");
      if (d?.available) { setSeries(d.series); setTimeline(d.timeline); return d; }
    } catch { /* ignore */ }
    setSeries(null); setTimeline(null);
    return null;
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (f.size > 6 * 1024 * 1024) { toast.error("Image too large (max 6MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setChart(reader.result);
    reader.readAsDataURL(f);
  };

  const autoGrab = async () => {
    const addr = address.trim() || piece?.address;
    if (!addr) { toast.error("Add a token address first"); return; }
    setGrabbing(true);
    toast.loading("Grabbing the chart…", { id: "grab" });
    try {
      const d = await loadSeries(addr);
      if (!d) { toast.error("No chart data available for that token", { id: "grab" }); return; }
      const url = renderPriceChart(d.series, { symbol: d.symbol || piece?.coin?.symbol, timeline: d.timeline });
      if (!url) { toast.error("Couldn't draw the chart", { id: "grab" }); return; }
      setChart(url);
      toast.success("Chart grabbed — attached to the card", { id: "grab" });
    } catch {
      toast.error("Chart grab failed", { id: "grab" });
    } finally {
      setGrabbing(false);
    }
  };

  const generate = async (overrideMode) => {
    if (!subject.trim() && !address.trim()) { toast.error("Enter a subject or a token address"); return; }
    const m = overrideMode || mode;
    const hasAddr = !!address.trim();
    if (!hasAddr) { setSeries(null); setTimeline(null); setChart(null); }
    setBusy(true);
    toast.loading(m === "thread" ? "Writing the thread…" : "Writing the story…", { id: "custom" });
    try {
      const job = await generateCustomEditorial({ subject: subject.trim(), address: address.trim(), angle, mode: m });
      const d = await pollJob(job.id);
      setPiece(d);
      setMode(m);
      if (d.address) loadSeries(d.address);
      else { setSeries(null); setTimeline(null); }
      setLibRefresh((n) => n + 1);
      toast.success(m === "thread" ? "Thread ready" : "Story ready", { id: "custom" });
    } catch (e) {
      toast.error(e?.message === "job-failed" ? (e.detail || "Generation failed") : (e?.response?.data?.detail || "Generation failed"), { id: "custom" });
    } finally {
      setBusy(false);
    }
  };

  const pollJob = async (sid) => {
    const deadline = Date.now() + 5 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000));
      let d;
      try { d = await fetchCustomStoryJob(sid); } catch { continue; }
      if (d.status === "done") return d;
      if (d.status === "failed") {
        const err = new Error("job-failed"); err.detail = d.error; throw err;
      }
    }
    const err = new Error("job-failed"); err.detail = "Still writing — check the Story Library in a minute"; throw err;
  };

  const loadFromLibrary = async (sid) => {
    toast.loading("Loading story…", { id: "load" });
    try {
      const d = await fetchCustomStory(sid);
      setSubject(d.subject || "");
      setAddress(d.address || "");
      setAngle(d.angle || "case-study");
      setChart(null);
      hydrate(d);
      toast.success("Loaded", { id: "load" });
      cardRef.current?.scrollIntoView?.({ behavior: "smooth" });
    } catch {
      toast.error("Couldn't load that story", { id: "load" });
    }
  };

  const isThread = piece?.mode === "thread" && Array.isArray(piece?.tweets) && piece.tweets.length > 0;
  const headline = piece ? cleanHeadline(piece.title || "") : "";
  const hook = piece ? (isThread ? (piece.tweets[1] || piece.tweets[0] || "") : pullHook(piece.body || "")) : "";
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "https://alphaapeing.com";
  const sym = piece?.coin?.symbol;

  const copy = () => {
    const txt = isThread ? piece.tweets.map((t, i) => `${i + 1}/${piece.tweets.length} ${t}`).join("\n\n") : piece?.body;
    if (!txt) return;
    navigator.clipboard.writeText(txt).then(() => toast.success("Copied — paste it into your post")).catch(() => toast.error("Copy failed"));
  };
  const postToX = () => {
    if (!headline) return;
    const text = `${headline}\n\n📊 via @AlphaApingrules`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,noreferrer");
  };
  const downloadCard = async () => {
    if (!cardRef.current) return;
    toast.loading("Building card…", { id: "ccard" });
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, width: 1200, height: 675 });
      const a = document.createElement("a");
      a.download = `alphaapeing-custom-${sym || "story"}.png`;
      a.href = dataUrl; a.click();
      toast.success("Card downloaded — attach it to your post", { id: "ccard" });
    } catch {
      toast.error("Couldn't build the card", { id: "ccard" });
    }
  };

  return (
    <div className="space-y-4">
      <div className="border border-neon/25 bg-neon/[0.03] p-5 sm:p-6 space-y-4" data-testid="custom-story-panel">
        <div className="flex items-center gap-2">
          <PencilSimpleIcon size={22} weight="fill" className="text-neon" />
          <h2 className="font-heading font-black text-xl tracking-tight">Write a custom story</h2>
        </div>
        <p className="text-sm text-muted-foreground -mt-1 leading-relaxed">
          Give the newsroom a subject — a token, a narrative, a blow-up like the one you just caught. Paste the CA to auto-pull its live on-chain facts, auto-grab its chart, and see exactly when it turned.
        </p>

        <div className="space-y-3">
          <textarea
            data-testid="custom-subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            rows={3}
            placeholder="e.g. This token trended on X as 'the next Ansem', ripped to a top, then rugged. What did the on-chain signals show, and what's the lesson?"
            className="w-full bg-black/40 border border-white/12 focus:border-neon/50 outline-none px-3 py-2.5 text-sm resize-y placeholder:text-muted-foreground/50"
          />

          <div className="flex items-center gap-2 bg-black/40 border border-white/12 focus-within:border-neon/50 px-3">
            <MagnifyingGlassIcon size={15} className="text-muted-foreground shrink-0" />
            <input
              data-testid="custom-address-input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Token contract address (optional — auto-pulls facts, chart & timeline)"
              className="w-full bg-transparent outline-none py-2.5 text-sm font-mono placeholder:font-sans placeholder:text-muted-foreground/50"
            />
          </div>

          <div className="flex flex-wrap gap-2" data-testid="custom-angle-row">
            {ANGLES.map((a) => (
              <button
                key={a.k}
                data-testid={`custom-angle-${a.k}`}
                onClick={() => setAngle(a.k)}
                className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${
                  angle === a.k ? "border-neon/60 bg-neon/[0.08] text-neon" : "border-white/12 text-muted-foreground hover:border-white/30 hover:text-white"
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>

          {/* Output mode toggle */}
          <div className="flex items-center gap-2" data-testid="custom-mode-row">
            <span className="text-[11px] font-mono uppercase tracking-wide text-muted-foreground/70 mr-1">Output</span>
            <button data-testid="custom-mode-story" onClick={() => setMode("story")} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors ${mode === "story" ? "border-neon/60 bg-neon/[0.08] text-neon" : "border-white/12 text-muted-foreground hover:border-white/30 hover:text-white"}`}>
              <ArticleIcon size={14} /> Article
            </button>
            <button data-testid="custom-mode-thread" onClick={() => setMode("thread")} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors ${mode === "thread" ? "border-neon/60 bg-neon/[0.08] text-neon" : "border-white/12 text-muted-foreground hover:border-white/30 hover:text-white"}`}>
              <ListNumbersIcon size={14} /> 🧵 Thread
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="custom-chart-input" />
            <button data-testid="custom-chart-grab-btn" onClick={autoGrab} disabled={grabbing} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-neon/30 text-neon hover:bg-neon/[0.08] transition-colors disabled:opacity-60">
              {grabbing ? <SpinnerGapIcon size={14} className="animate-spin" /> : <ChartLineUpIcon size={14} weight="fill" />} Auto-grab chart
            </button>
            {chart ? (
              <div className="flex items-center gap-3">
                <img src={chart} alt="chart" className="h-14 w-24 object-cover border border-white/15 rounded" data-testid="custom-chart-preview" />
                <button data-testid="custom-chart-remove" onClick={() => { setChart(null); if (fileRef.current) fileRef.current.value = ""; }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 hover:border-red-400/50 hover:text-red-300 transition-colors">
                  <TrashIcon size={14} /> Remove chart
                </button>
              </div>
            ) : (
              <button data-testid="custom-chart-btn" onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 hover:border-neon/40 hover:text-neon transition-colors">
                <UploadSimpleIcon size={14} /> Upload chart
              </button>
            )}
            <button
              data-testid="custom-generate-btn"
              onClick={() => generate()}
              disabled={busy}
              className="ml-auto flex items-center gap-2 px-5 py-2 bg-neon text-black font-semibold hover:bg-neon/90 transition-colors disabled:opacity-60"
            >
              {busy ? <SpinnerGapIcon size={16} className="animate-spin" /> : <PencilSimpleIcon size={16} weight="fill" />}
              {busy ? "Writing…" : piece ? (mode === "thread" ? "Rewrite thread" : "Rewrite story") : (mode === "thread" ? "Write thread" : "Write story")}
            </button>
          </div>
        </div>

        {piece?.body && (
          <div className="border-t border-white/10 pt-5 mt-2" data-testid="custom-story-output">
            {sym && (
              <span className="inline-block mb-3 px-2 py-0.5 text-[11px] font-mono border border-neon/30 text-neon">${String(sym).toUpperCase()}</span>
            )}
            {isThread ? (
              <StoryThread tweets={piece.tweets} />
            ) : (
              <div className="prose-degen">{renderMd(piece.body)}</div>
            )}

            {timeline && <RugTimeline timeline={timeline} symbol={sym} />}

            {chart && (
              <img src={chart} alt="attached chart" className="mt-4 w-full max-w-xl border border-white/12 rounded" data-testid="custom-story-chart" />
            )}

            <div className="flex flex-wrap items-center gap-2 mt-5 pt-4 border-t border-white/10">
              <button data-testid="custom-post-x-btn" onClick={postToX} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-neon text-black hover:bg-neon/90 transition-colors">
                <XLogoIcon size={14} weight="fill" /> Post to X
              </button>
              <button data-testid="custom-card-btn" onClick={downloadCard} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 hover:border-neon/40 hover:text-neon transition-colors">
                <ImageIcon size={14} /> Share card{chart ? " + chart" : ""}
              </button>
              <button data-testid="custom-copy-btn" onClick={copy} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 hover:border-neon/40 hover:text-neon transition-colors">
                <CopyIcon size={14} /> Copy
              </button>
              <button data-testid="custom-regen-btn" onClick={() => generate()} disabled={busy} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 hover:border-neon/40 hover:text-neon transition-colors disabled:opacity-60">
                <ArrowClockwiseIcon size={14} /> Rewrite
              </button>
            </div>
          </div>
        )}

        {/* Off-screen branded share card (1200×675) captured to PNG */}
        {piece?.body && (
          <div style={{ position: "fixed", left: "-10000px", top: 0, pointerEvents: "none" }} aria-hidden>
            <div
              ref={cardRef}
              data-testid="custom-share-card"
              style={{ width: 1200, height: 675, backgroundColor: "#05070c" }}
              className="relative overflow-hidden flex flex-col justify-between"
            >
              <div style={{ position: "absolute", inset: 0, background: "radial-gradient(1100px 500px at 85% -10%, rgba(0,255,136,0.16), transparent 60%)" }} />
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 10, backgroundColor: "#00ff88" }} />
              <div style={{ position: "relative", padding: "56px 72px 0 80px" }} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span style={{ width: 18, height: 18, borderRadius: 9999, backgroundColor: "#00ff88", boxShadow: "0 0 20px rgba(0,255,136,0.9)" }} />
                  <span className="font-heading font-black" style={{ fontSize: 30, letterSpacing: "-0.02em", color: "#ffffff" }}>ALPHAAPEING</span>
                  <span className="font-mono" style={{ fontSize: 15, color: "#00ff88", letterSpacing: "0.12em" }}>· DEGEN SCORE</span>
                </div>
                <span className="font-mono uppercase" style={{ fontSize: 15, color: "rgba(255,255,255,0.55)", letterSpacing: "0.12em" }}>
                  ✍️ Newsroom{sym ? ` · $${String(sym).toUpperCase()}` : ""}
                </span>
              </div>

              {chart ? (
                <div style={{ position: "relative", padding: "0 80px", display: "flex", gap: 44, alignItems: "center" }}>
                  <div style={{ flex: "1 1 0", minWidth: 0 }}>
                    <h2 className="font-heading font-black" style={{ fontSize: 46, lineHeight: 1.06, letterSpacing: "-0.03em", color: "#ffffff", maxHeight: 300, overflow: "hidden" }}>
                      {headline}
                    </h2>
                    {hook && (
                      <p style={{ marginTop: 20, fontSize: 22, lineHeight: 1.4, color: "#9defc4", maxHeight: 130, overflow: "hidden" }}>{hook}</p>
                    )}
                  </div>
                  <div style={{ width: 470, height: 320, flexShrink: 0, border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, overflow: "hidden", backgroundColor: "#000" }}>
                    <img src={chart} alt="chart" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  </div>
                </div>
              ) : (
                <div style={{ position: "relative", padding: "0 80px" }}>
                  <h2 className="font-heading font-black" style={{ fontSize: 62, lineHeight: 1.04, letterSpacing: "-0.03em", color: "#ffffff", maxHeight: 340, overflow: "hidden" }}>
                    {headline}
                  </h2>
                  {hook && (
                    <p style={{ marginTop: 28, fontSize: 27, lineHeight: 1.4, color: "#9defc4", maxWidth: 1040, maxHeight: 120, overflow: "hidden" }}>{hook}</p>
                  )}
                </div>
              )}

              <div style={{ position: "relative", padding: "0 80px 56px 80px" }} className="flex items-center justify-between">
                <span className="font-mono" style={{ fontSize: 22, color: "#ffffff", fontWeight: 700 }}>alphaapeing.com</span>
                <span className="font-mono" style={{ fontSize: 20, color: "rgba(255,255,255,0.55)" }}>@AlphaApingrules</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <StoryLibrary onLoad={loadFromLibrary} refreshKey={libRefresh} />
    </div>
  );
}
